import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Disc3, Loader2 } from "lucide-react";

const BG_URL =
  "https://images.unsplash.com/photo-1721589607926-f792eea1c39d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxoZWFkcGhvbmVzJTIwZGFyayUyMHN0dWRpb3xlbnwwfHx8fDE3Nzg3Mzg1ODR8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#0A0A0A] text-white">
      <div
        className="hidden lg:block lg:col-span-3 relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,0.9) 100%), url(${BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="login-hero"
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <span className="relative inline-flex h-10 w-10 items-center justify-center bg-[#FF3B30] rounded-sm">
              <Disc3 className="h-6 w-6 text-white" strokeWidth={2.2} />
            </span>
            <div className="font-display font-black text-2xl tracking-tighter">LYRIQ</div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-4">
              // your personal lyric studio
            </div>
            <h1 className="font-display font-black tracking-tighter text-5xl xl:text-7xl leading-[0.9] max-w-lg">
              Upload. Play. <span className="text-[#FF3B30]">Sing every word.</span>
            </h1>
            <p className="mt-6 text-white/60 max-w-md text-base">
              Drop in an MP3, paste a .lrc or hit auto-fetch — and watch the lyrics roll in time with the music.
            </p>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-6 md:p-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm space-y-7"
          data-testid="login-form"
        >
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              // step 01 — auth
            </div>
            <h2 className="mt-2 font-display font-black text-4xl tracking-tighter">Sign in</h2>
            <p className="text-white/50 mt-2 text-sm">
              New here?{" "}
              <Link to="/register" className="text-[#FF3B30] hover:underline" data-testid="link-to-register">
                Create an account
              </Link>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@studio.com"
                className="mt-2 bg-transparent border-white/10 text-white placeholder:text-white/30 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="••••••••"
                className="mt-2 bg-transparent border-white/10 text-white placeholder:text-white/30 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm h-11"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <div
              className="border border-[#FF3B30]/60 bg-[#FF3B30]/10 text-white px-3 py-2 text-sm rounded-sm font-mono"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-sm font-display font-bold tracking-wide uppercase text-sm transition-colors"
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter studio"}
          </Button>

          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30 text-center">
            try admin@lyriq.app / admin123
          </p>
        </form>
      </div>
    </div>
  );
}
